import type { MouseEvent } from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import type { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import type { Vehicle } from "../../providers/vehiclesProvider";
import { VehicleIcon } from "../../icons/VehicleIcon";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import type { VehicleListVariant } from "./VehiclesDataGrid";
import { formatGearType } from "./vehicleFormatters";

type VehicleListItemVehicle = Vehicle & { deletedAt?: DateTime | Date | string | null };

export function VehicleMobileListItem(props: {
  vehicle: VehicleListItemVehicle;
  drivingSchool?: DrivingSchool;
  instructorNamesById: Record<string, string>;
  variant?: VehicleListVariant;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  const { vehicle, drivingSchool, instructorNamesById, variant = "active", onClick } = props;
  const theme = useTheme();
  const showInstructors = variant === "active" && vehicle.type !== "simulator";
  const entitledInstructorIds = showInstructors ? getEntitledInstructorIds(vehicle) : [];
  const instructorsLabel = _formatInstructorNames(entitledInstructorIds, instructorNamesById);
  const showInstructorLabel = showInstructors && (entitledInstructorIds.length === 0 || !!instructorsLabel);
  const primaryDetails = [
    vehicle.type !== "simulator" ? vehicle.plateNumber : undefined,
    vehicle.type === "car" ? formatGearType(vehicle.gearType) : undefined,
    vehicle.type === "simulator" ? drivingSchool?.branchesById[vehicle.branchId]?.name : undefined,
  ].filter(Boolean);
  const deletedAt = variant === "deleted" ? _formatDeletedAt(vehicle.deletedAt) : undefined;

  return (
    <ButtonBase
      component="div"
      disabled={!onClick}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        alignItems: "stretch",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "block",
        px: 0,
        py: 1,
        textAlign: "left",
        width: "100%",
        "&.Mui-disabled": {
          color: "inherit",
        },
      }}
    >
      <Column sx={{ gap: 0.5, minWidth: 0 }}>
        <Row sx={{ alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: theme.palette.action.hover,
              borderRadius: "50%",
              color: theme.palette.text.secondary,
              display: "flex",
              flexShrink: 0,
              height: 40,
              justifyContent: "center",
              width: 40,
            }}
          >
            <VehicleIcon vehicle={vehicle} fontSize="small" />
          </Box>
          <Column sx={{ gap: 0.25, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {vehicle.name}
            </Typography>
            {primaryDetails.length > 0 && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {primaryDetails.join(" · ")}
              </Typography>
            )}
          </Column>
        </Row>
        {vehicle.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {vehicle.notes}
          </Typography>
        )}
        {(showInstructorLabel || deletedAt) && (
          <Row sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            {showInstructorLabel && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {entitledInstructorIds.length === 0 ? "Keine Fahrlehrer" : instructorsLabel}
              </Typography>
            )}
            {deletedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, ml: showInstructors ? 0 : "auto" }}
              >
                Gelöscht {deletedAt}
              </Typography>
            )}
          </Row>
        )}
      </Column>
    </ButtonBase>
  );
}

export function getEntitledInstructorIds(vehicle: Vehicle): string[] {
  const entitledInstructorIds = "entitledInstructorIds" in vehicle ? vehicle.entitledInstructorIds : [];
  if (!Array.isArray(entitledInstructorIds)) {
    return [];
  }
  return entitledInstructorIds;
}

function _formatInstructorNames(
  entitledInstructorIds: string[],
  instructorNamesById: Record<string, string>,
): string | undefined {
  const instructorNames = entitledInstructorIds.map((id) => instructorNamesById[id]).filter(Boolean);
  if (instructorNames.length === 0) {
    return undefined;
  }
  if (instructorNames.length === 1) {
    return instructorNames[0];
  }
  const remainingInstructors = instructorNames.length - 1;
  return `${instructorNames[0]} + ${remainingInstructors === 1 ? "1 weiterer" : `${remainingInstructors} weitere`}`;
}

function _formatDeletedAt(value: unknown): string | undefined {
  if (DateTime.isDateTime(value)) {
    return value.toFormat("dd.MM.yyyy");
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat("dd.MM.yyyy");
  }
  if (typeof value === "string") {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat("dd.MM.yyyy") : undefined;
  }
  return undefined;
}
